// Dealer-side "Locations" admin section. Sits inside the
// AdminPanelContent → "Org Administration" accordion stack, between
// GHL CRM Connections and Organization lifecycle.
//
// Responsibilities:
//   - Section chrome (title / description / refresh + new buttons)
//   - Wire useOrgLocationsTab to the list + form components
//   - Inline error banner + "show deactivated" toggle
//
// Soft delete is the only delete affordance — per docs/workflows/
// org_locations_and_plants.md §10, hard-delete would cascade-orphan
// leads / pipelines / mfg overlays that FK to locations.id.
import { useMemo } from "react";
import { useOrgLocationsTab } from "./useOrgLocationsTab";
import { OrgLocationsList } from "./OrgLocationsList";
import { OrgLocationsForm } from "./OrgLocationsForm";
import { PaginationBar } from "@/components/common/PaginationBar";
import { usePagination } from "@/components/common/usePagination";
import type { OrgLocation } from "@/lib/orgLocations/orgLocationsTypes";

const compactPrimaryBtn =
  "rounded-lg bg-indigo-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60";
const compactGhostBtn =
  "rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-700";

type Props = {
  organizationId: string;
  /** Drop the outer card chrome (used when nested in the Admin accordion). */
  plain?: boolean;
  /** Slice 14: when false, hides the Deactivate/Reactivate row buttons
   *  AND the "Show deactivated" toggle. Used by the trainer-side
   *  Location Tools surface where trainer/marketing roles can add+edit
   *  but cannot soft-delete (backend enforces the same boundary). */
  allowDeactivate?: boolean;
  /** Slice 14: fires after any successful mutation. Used by hosts that
   *  mirror the location list elsewhere (e.g. the trainer Location
   *  Tools panel's "edit links by location" dropdown) so they can
   *  refetch their own copy. */
  onLocationsChanged?: () => void;
};

export function OrgLocationsSection({ organizationId, plain, allowDeactivate = true, onLocationsChanged }: Props) {
  const m = useOrgLocationsTab({ onLocationsChanged });

  // Currently the active workspace org is implicit (read server-side
  // from the auth headers); `organizationId` is here purely for the
  // accordion-section interface contract so we can target a specific
  // org later if/when platform staff need to manage a subscriber from
  // a different workspace. Suppress the unused-arg lint without
  // changing the public type.
  void organizationId;

  const totalCount = m.locations.length;
  const visibleCount = m.visibleLocations.length;

  // Slice 14: client-side pagination over the already-filtered
  // visibleLocations. Default page size = 5 (matches the manufacturing
  // dealer-locations slider precedent from slice 12).
  const pg = usePagination(m.visibleLocations, 5);

  // Slice 13a: corporate-HQ anchor for one-click address prefill on
  // virtual / corporate rows. Picks the first active corporate-typed
  // row that has a real street address. Excluded from the EDIT
  // anchor when the row being edited IS the anchor (don't prefill a
  // location from itself). CREATE always sees the anchor (no row id).
  const createAnchor: OrgLocation | null = useMemo(() => {
    return (
      m.locations.find(
        (l) => l.is_active && l.location_type === "corporate" && !!l.address_1
      ) ?? null
    );
  }, [m.locations]);

  const editAnchor: OrgLocation | null = useMemo(() => {
    if (!createAnchor) return null;
    if (m.editingId === createAnchor.id) return null;
    return createAnchor;
  }, [createAnchor, m.editingId]);

  return (
    <section className={plain ? "space-y-3" : "p-4 rounded-xl bg-white border border-gray-200 space-y-3 dark:border-zinc-700 dark:bg-zinc-900/45"}>
      {!plain ? (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-zinc-100">Locations</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Manage retail lots, online-only sales locations, plants, and corporate offices owned by your organization.
          </p>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-gray-600 dark:text-zinc-400">
          {m.listBusy
            ? "Loading…"
            : m.showInactive
            ? `${visibleCount} location${visibleCount === 1 ? "" : "s"} (including deactivated)`
            : `${visibleCount} active location${visibleCount === 1 ? "" : "s"}${
                totalCount > visibleCount ? ` (+${totalCount - visibleCount} deactivated, hidden)` : ""
              }`}
        </div>
        <div className="flex items-center gap-2">
          {allowDeactivate ? (
            <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-zinc-400">
              <input
                type="checkbox"
                checked={m.showInactive}
                onChange={(e) => m.setShowInactive(e.target.checked)}
                disabled={m.listBusy || m.actionBusy}
              />
              Show deactivated
            </label>
          ) : null}
          <button
            type="button"
            className={compactGhostBtn}
            onClick={() => void m.reload()}
            disabled={m.listBusy || m.actionBusy}
          >
            {m.listBusy ? "Refreshing…" : "Refresh"}
          </button>
          <button
            type="button"
            className={compactPrimaryBtn}
            onClick={m.creating ? m.cancelCreate : m.startCreate}
            disabled={m.actionBusy || m.listBusy}
          >
            {m.creating ? "Cancel new" : "+ New location"}
          </button>
        </div>
      </div>

      {m.localError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
          {m.localError}
        </div>
      ) : null}

      {m.creating ? (
        <OrgLocationsForm
          mode="create"
          form={m.newForm}
          setForm={m.setNewForm}
          onSubmit={m.submitCreate}
          onCancel={m.cancelCreate}
          busy={m.actionBusy}
          corporateAnchor={createAnchor}
        />
      ) : null}

      <OrgLocationsList
        m={m}
        locations={pg.paginatedItems}
        allowDeactivate={allowDeactivate}
        corporateAnchor={editAnchor}
      />

      <PaginationBar
        pageIndex={pg.pageIndex}
        pageCount={pg.pageCount}
        pageSize={pg.pageSize}
        totalCount={pg.totalCount}
        firstShown={pg.firstShown}
        lastShown={pg.lastShown}
        onPageChange={pg.setPageIndex}
        onPageSizeChange={pg.setPageSize}
        noun="locations"
        alwaysShow
      />
    </section>
  );
}
